/**
 * Contract Termination Hook
 * US-CONT-006: Terminate Contract
 * Custom hook for managing contract termination operations
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { contractTerminationApi } from '../api/contractTerminationApi';
import type {
  TerminateContractRequest,
  TerminateContractResponse,
  TerminationHistory,
  TerminationPermission,
  TerminationFormState
} from '../types/contract-termination.types';
import type { ExpiringContract } from '@/features/contracts/expiring/types/expiringContracts.types';

/**
 * Hook for contract termination operations
 * @returns Termination functions and state
 */
export function useContractTermination() {
  const [isTerminating, setIsTerminating] = useState(false);
  const [terminationError, setTerminationError] = useState<string | null>(null);

  /**
   * Terminate a contract
   * @param contractId - Contract ID
   * @param formData - Termination form data
   * @returns Termination response
   */
  const terminateContract = useCallback(
    async (contractId: string, formData: TerminationFormState): Promise<TerminateContractResponse | null> => {
      setIsTerminating(true);
      setTerminationError(null);

      try {
        const request: TerminateContractRequest = {
          contractId,
          reason: formData.reason as any, // Type assertion needed due to empty string in form state
          notes: formData.notes,
          authorizationCode: formData.authorizationCode
        };

        const response = await contractTerminationApi.terminateContract(request);

        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to terminate contract');
        }

        toast.success('Contract Terminated', {
          description: `Contract ${response.data.contractNumber} has been terminated successfully.`
        });

        return response.data;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to terminate contract';
        setTerminationError(errorMessage);

        toast.error('Termination Failed', {
          description: errorMessage
        });

        return null;
      } finally {
        setIsTerminating(false);
      }
    },
    []
  );

  /**
   * Check if user can terminate a contract
   * @param contractId - Contract ID
   * @returns Permission check result
   */
  const checkTerminationPermission = useCallback(
    async (contractId: string): Promise<TerminationPermission | null> => {
      try {
        const response = await contractTerminationApi.canTerminateContract(contractId);

        if (!response.success || !response.data) {
          console.warn('Failed to check termination permission:', response.error);
          return null;
        }

        return response.data;
      } catch (error) {
        console.error('Error checking termination permission:', error);
        return null;
      }
    },
    []
  );

  /**
   * Get termination history for a contract
   * @param contractId - Contract ID
   * @returns Termination history
   */
  const getTerminationHistory = useCallback(
    async (contractId: string): Promise<TerminationHistory | null> => {
      try {
        const response = await contractTerminationApi.getTerminationHistory(contractId);

        if (!response.success || !response.data) {
          // Not an error - contract might not be terminated
          return null;
        }

        return response.data;
      } catch (error) {
        console.error('Error fetching termination history:', error);
        return null;
      }
    },
    []
  );

  /**
   * Send termination notification to customer
   * @param contractId - Contract ID
   * @returns Notification result
   */
  const sendTerminationNotification = useCallback(async (contractId: string): Promise<boolean> => {
    try {
      const response = await contractTerminationApi.sendTerminationNotification(contractId);

      if (!response.success || !response.data?.sent) {
        toast.error('Notification Failed', {
          description: response.error || 'Failed to send termination notification'
        });
        return false;
      }

      toast.success('Notification Sent', {
        description: 'Customer has been notified of the contract termination'
      });

      return true;
    } catch (error) {
      toast.error('Notification Failed', {
        description: error instanceof Error ? error.message : 'Failed to send notification'
      });
      return false;
    }
  }, []);

  return {
    terminateContract,
    checkTerminationPermission,
    getTerminationHistory,
    sendTerminationNotification,
    isTerminating,
    terminationError,
    clearError: () => setTerminationError(null)
  };
}

/**
 * Hook to check if contract can be terminated
 * @param contract - Contract object
 * @returns Whether contract can be terminated
 */
export function useCanTerminateContract(contract: ExpiringContract | null): boolean {
  if (!contract) return false;

  // Can only terminate ACTIVE or EXPIRING_SOON contracts
  return contract.status === 'ACTIVE' || contract.status === 'EXPIRING_SOON';
}

/**
 * Hook to manage termination dialog state
 * @returns Dialog state and handlers
 */
export function useTerminationDialog() {
  const [selectedContract, setSelectedContract] = useState<ExpiringContract | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openDialog = useCallback((contract: ExpiringContract) => {
    setSelectedContract(contract);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    // Delay clearing contract to allow dialog close animation
    setTimeout(() => setSelectedContract(null), 200);
  }, []);

  return {
    selectedContract,
    isOpen,
    openDialog,
    closeDialog
  };
}

/**
 * Hook for termination with confirmation workflow
 * @param onSuccess - Callback after successful termination
 * @returns Termination workflow handlers
 */
export function useTerminationWorkflow(onSuccess?: (contractId: string) => void) {
  const { terminateContract, isTerminating, terminationError } = useContractTermination();
  const { selectedContract, isOpen, openDialog, closeDialog } = useTerminationDialog();

  const handleTerminate = useCallback(
    async (formData: TerminationFormState) => {
      if (!selectedContract) return;

      const result = await terminateContract(selectedContract.id, formData);

      if (result) {
        closeDialog();
        onSuccess?.(selectedContract.id);
      }
    },
    [selectedContract, terminateContract, closeDialog, onSuccess]
  );

  return {
    selectedContract,
    isDialogOpen: isOpen,
    isTerminating,
    terminationError,
    openTerminationDialog: openDialog,
    closeTerminationDialog: closeDialog,
    handleTerminate
  };
}
